// Formatting utility functions for Optimus OS Affiliate System

/**
 * Format currency with proper locale and currency symbol
 */
export function formatCurrency(amount, currency = 'USD') {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return '$0.00';
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/**
 * Format large numbers with K, M, B suffixes
 */
export function formatCompactNumber(num) {
  if (num === null || num === undefined || isNaN(num)) {
    return '0';
  }

  if (num >= 1000000000) {
    return (num / 1000000000).toFixed(1) + 'B';
  }
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toString();
}

/**
 * Format date to readable string
 */
export function formatDate(date, options = {}) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const defaultOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  };

  return new Intl.DateTimeFormat('en-US', defaultOptions).format(dateObj);
}

/**
 * Format date to relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  const now = new Date();
  const diffMs = now - dateObj;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);
  const diffWeek = Math.floor(diffDay / 7);
  const diffMonth = Math.floor(diffDay / 30);
  const diffYear = Math.floor(diffDay / 365);

  if (diffSec < 60) return 'Just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour > 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
  if (diffWeek < 4) return `${diffWeek} week${diffWeek > 1 ? 's' : ''} ago`;
  if (diffMonth < 12) return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
  return `${diffYear} year${diffYear > 1 ? 's' : ''} ago`;
}

/**
 * Format date and time
 */
export function formatDateTime(date) {
  if (!date) return 'N/A';

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (isNaN(dateObj.getTime())) {
    return 'Invalid Date';
  }

  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(dateObj);
}

/**
 * Format percentage
 */
export function formatPercentage(value, decimals = 1) {
  if (value === null || value === undefined || isNaN(value)) {
    return '0%';
  }
  return `${value.toFixed(decimals)}%`;
}

/**
 * Format product type to display name
 */
export function formatProductType(productType) {
  const typeMap = {
    'starter': 'Starter',
    'pro': 'Pro',
    'enterprise': 'Enterprise',
    'enterprise_seat': 'Enterprise Seat',
  };

  return typeMap[productType] || productType;
}

/**
 * Format status badge text
 */
export function formatStatus(status) {
  const statusMap = {
    'active': 'Active',
    'pending': 'Pending',
    'paid': 'Paid',
    'failed': 'Failed',
    'refunded': 'Refunded',
    'clawed_back': 'Clawed Back',
  };

  return statusMap[status] || status;
}

/**
 * Calculate and format trend indicator
 */
export function formatTrend(current, previous) {
  if (!previous || previous === 0) {
    return { value: 0, direction: 'neutral' };
  }

  const change = ((current - previous) / previous) * 100;
  const direction = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';

  return {
    value: Math.abs(change).toFixed(1),
    direction,
    text: `${direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→'} ${Math.abs(change).toFixed(1)}%`,
  };
}

/**
 * Truncate email for display
 */
export function truncateEmail(email, maxLength = 25) {
  if (!email || email.length <= maxLength) {
    return email;
  }

  const [name, domain] = email.split('@');
  if (name.length + domain.length + 1 <= maxLength) {
    return email;
  }

  const truncatedName = name.substring(0, Math.max(3, maxLength - domain.length - 4));
  return `${truncatedName}...@${domain}`;
}

export default {
  formatCurrency,
  formatCompactNumber,
  formatDate,
  formatRelativeTime,
  formatDateTime,
  formatPercentage,
  formatProductType,
  formatStatus,
  formatTrend,
  truncateEmail,
};
